import { useMutation } from "@tanstack/react-query";
import { CheckIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/button";
import { useGoogleTagManager } from "@/features/auth/subscription/hooks/useGoogleTagManager.ts";
import { usePageTitle } from "@/hooks/usePageTitle";

import { resendInviteEmail } from "../api/resendInvite.api";
import {
  formatChargeDate,
  formatMoney,
  intervalFromBillingCycle,
  intervalLabels,
  nextChargeDate,
} from "../utils/checkoutPricing";
import { toIsoCurrency } from "../utils/purchaseEvent";
import { CheckoutLayout } from "./CheckoutLayout";

const SENT_LABEL_DURATION_MS = 2200;

interface CheckEmailScreenProps {
  billingCycle?: string;
  currency?: string;
  planName?: string;
  price?: string;
}

function readSuccessData(): {
  couponLabel?: string;
  email?: string;
  isExistingAccount?: boolean;
} {
  try {
    return (
      JSON.parse(localStorage.getItem("subscriptionSuccessData") ?? "{}") ?? {}
    );
  } catch {
    return {};
  }
}

export function CheckEmailScreen({
  billingCycle,
  currency,
  planName,
  price,
}: CheckEmailScreenProps) {
  usePageTitle("Check your email");
  useGoogleTagManager("GTM-5X67HTS");
  const [{ couponLabel, email, isExistingAccount }] = useState(readSuccessData);
  const [isSentLabelVisible, setIsSentLabelVisible] = useState(false);
  const resendInvite = useMutation({
    mutationFn: resendInviteEmail,
    onSuccess: () => setIsSentLabelVisible(true),
  });

  useEffect(() => {
    if (!isSentLabelVisible) return;
    const timeout = setTimeout(
      () => setIsSentLabelVisible(false),
      SENT_LABEL_DURATION_MS,
    );

    return () => clearTimeout(timeout);
  }, [isSentLabelVisible]);

  const interval = intervalFromBillingCycle(billingCycle);
  const charged = Number(price);
  const rows = [
    { label: "Plan", value: planName },
    {
      label: "Billing",
      value:
        price && Number.isFinite(charged)
          ? `${formatMoney(charged, toIsoCurrency(currency, "USD"))} / ${intervalLabels(interval).unit}`
          : undefined,
    },
    { label: "Applied coupon", value: couponLabel },
    {
      label: "Next charge",
      value: billingCycle
        ? formatChargeDate(nextChargeDate(interval, new Date()))
        : undefined,
    },
  ].filter((row) => row.value);

  return (
    <CheckoutLayout>
      <div className="mx-auto flex w-full max-w-[484px] flex-col gap-8 rounded-[20px] border border-border-secondary bg-bg-primary p-8 shadow-sm">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-display-xs font-semibold text-text-primary">
            Please check your email
          </h1>
          <p className="text-sm text-text-secondary">
            We&apos;ve sent a confirmation to{" "}
            <span className="font-semibold text-text-primary">
              {email || "your email"}
            </span>{" "}
            to verify your account and create a password.
          </p>
        </div>

        {rows.length > 0 && (
          <dl className="overflow-hidden rounded-xl border border-border-secondary">
            {rows.map((row) => (
              <div
                className="flex items-center justify-between border-b border-border-secondary px-3 py-3 last:border-b-0"
                key={row.label}
              >
                <dt className="text-sm text-text-secondary">{row.label}</dt>
                <dd className="text-sm font-semibold text-text-primary">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <div className="flex flex-col gap-5">
          <span className="text-xs font-semibold text-text-secondary">
            What happens next
          </span>
          <ol className="flex flex-col">
            <li className="flex gap-4">
              <div className="flex flex-col items-center">
                <span className="flex size-8 items-center justify-center rounded-full bg-bg-success-secondary text-fg-success-primary">
                  <CheckIcon aria-hidden className="size-4" />
                </span>
                <span aria-hidden className="w-px flex-1 bg-border-secondary" />
              </div>
              <div className="flex flex-col gap-1 pb-8">
                <span className="text-sm font-semibold text-text-primary">
                  Confirm your email
                </span>
                <span className="text-sm text-text-secondary">
                  Click the link we just sent to verify your account and create
                  a password to login.
                </span>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-bg-secondary text-xs font-semibold text-text-secondary">
                2
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-text-primary">
                  Book a welcome call
                </span>
                <span className="text-sm text-text-secondary">
                  Pick a time with our team to get started.
                </span>
              </div>
            </li>
          </ol>
        </div>

        <div className="flex flex-col gap-3 border-t border-border-secondary pt-8">
          {/* Existing accounts already have a password, so there is no invite to resend. */}
          {!isExistingAccount && (
            <>
              <Button
                className="w-full"
                color="gray"
                isDisabled={
                  !email || isSentLabelVisible || resendInvite.isPending
                }
                isLoading={resendInvite.isPending}
                loadingLabel="Sending"
                onPress={() => email && resendInvite.mutate(email)}
                size="lg"
                variant="secondary"
              >
                {isSentLabelVisible
                  ? "Confirmation email sent!"
                  : "Resend confirmation email"}
              </Button>
              {resendInvite.isError && (
                <p className="text-center text-sm text-text-error-primary">
                  Couldn&apos;t resend right now. Please try again in a moment.
                </p>
              )}
            </>
          )}
          <p className="text-center text-sm text-text-secondary">
            Make sure to check your spam folders.
          </p>
        </div>
      </div>
    </CheckoutLayout>
  );
}
